import { db, doc, setDoc, serverTimestamp } from "./firebase";
import { askMentor as askMentorCallable } from "./functions";
import { audit } from "./audit";

/**
 * Ask the AI mentor a question scoped to a course module.
 * Returns { answer } on success. Throws on auth / network errors.
 */
export async function askMentor({ moduleId, question }) {
  if (!moduleId) throw new Error("moduleId required");
  if (!question || !question.trim()) throw new Error("question required");
  const res = await askMentorCallable({ moduleId, question: question.trim() });
  return res || { answer: "" };
}

/**
 * Record an enrollment for the signed-in user. v1: write-only flag,
 * no payments. Document path: enrollments/{uid}_{moduleId} so a single
 * doc is owned by the user and rules can match on uid prefix.
 */
export async function enrollInModule({ uid, moduleId, moduleTitle }) {
  if (!uid) throw new Error("uid required");
  if (!moduleId) throw new Error("moduleId required");
  if (!db) return;
  const docId = `${uid}_${moduleId}`;
  await setDoc(
    doc(db, "enrollments", docId),
    {
      uid,
      moduleId,
      moduleTitle: moduleTitle || null,
      status: "enrolled",
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );
  await audit({
    action: "enrollment.created",
    actor: { uid },
    targetCollection: "enrollments",
    targetId: docId,
    metadata: { moduleId, moduleTitle: moduleTitle || null },
  });
}

/**
 * Lazy GA4 logEvent helper. Safe at module top-level — no firebase/analytics
 * import until called, so SSR + the build are unaffected. Silently noops
 * server-side and when measurementId is missing.
 */
export async function trackEvent(name, params) {
  if (typeof window === "undefined") return;
  try {
    const { getAnalytics, logEvent, isSupported } = await import("firebase/analytics");
    const supported = await isSupported();
    if (!supported) return;
    const a = getAnalytics();
    logEvent(a, name, params || {});
  } catch (e) {
    // Analytics must never block UX.
    // eslint-disable-next-line no-console
    console.debug("trackEvent skipped", name, e && e.message);
  }
}
